import React, { useEffect, useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Layers, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchTrendingMetrics, fetchTrendingData } from '../lib/api';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import { format } from 'date-fns';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function MetricSummary({ data, label, color }: { data: number[]; label: string; color: string }) {
  if (data.length === 0) return null;
  const current = data[data.length - 1];
  const min = Math.min(...data);
  const max = Math.max(...data);
  const avg = data.reduce((a, b) => a + b, 0) / data.length;
  const prev = data.length >= 2 ? data[data.length - 2] : current;
  const diff = prev !== 0 ? ((current - prev) / Math.abs(prev)) * 100 : 0;
  const TrendIcon = diff > 0.5 ? TrendingUp : diff < -0.5 ? TrendingDown : Minus;
  const trendColor = diff > 0.5 ? 'text-green-600 dark:text-green-400' : diff < -0.5 ? 'text-red-500 dark:text-red-400' : 'text-gray-400';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-bold text-gray-900 dark:text-gray-100">{current.toFixed(1)}</span>
        <span className={`flex items-center gap-0.5 text-xs font-medium ${trendColor}`}>
          <TrendIcon className="w-3 h-3" /> {Math.abs(diff).toFixed(1)}%
        </span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[10px] text-gray-500 dark:text-gray-400">
        <div><span className="block font-medium text-gray-700 dark:text-gray-300">{min.toFixed(1)}</span>Min</div>
        <div><span className="block font-medium text-gray-700 dark:text-gray-300">{avg.toFixed(1)}</span>Avg</div>
        <div><span className="block font-medium text-gray-700 dark:text-gray-300">{max.toFixed(1)}</span>Max</div>
      </div>
    </div>
  );
}

export default function TrendingPage() {
  const [metrics, setMetrics] = useState<string[]>([]);
  const [selectedMetric, setSelectedMetric] = useState('');
  const [compareMetrics, setCompareMetrics] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [compareData, setCompareData] = useState<Record<string, number[]>>({});
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);
  const [showCompare, setShowCompare] = useState(false);
  const { selectedTenantId } = useTenant();
  const { t } = useTranslation();
  const { isDark } = useTheme();

  useEffect(() => {
    fetchTrendingMetrics()
      .then((m: string[]) => {
        setMetrics(m || []);
        if (m && m.length > 0) setSelectedMetric(m[0]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedMetric) return;
    setLoading(true);
    const metricsToFetch = showCompare ? [selectedMetric, ...compareMetrics] : [selectedMetric];

    Promise.all(
      metricsToFetch.map((m) =>
        fetchTrendingData(m, { limit, tenant_id: selectedTenantId || undefined })
          .then((points: any[]) => ({ metric: m, points: (points || []).reverse() }))
          .catch(() => ({ metric: m, points: [] }))
      )
    ).then((results) => {
      // Build merged chart data using the primary metric's timestamps
      const primary = results[0];
      const formatted = primary.points.map((p: any, i: number) => {
        const row: any = {
          date: p.created_at ? format(new Date(p.created_at), 'MMM d') : '',
          time: p.created_at ? format(new Date(p.created_at), 'HH:mm') : '',
          [primary.metric]: p.value,
        };
        results.slice(1).forEach((r) => {
          if (r.points[i]) row[r.metric] = r.points[i].value;
        });
        return row;
      });
      setChartData(formatted);

      // Store raw values for summary cards
      const rawData: Record<string, number[]> = {};
      results.forEach((r) => { rawData[r.metric] = r.points.map((p: any) => p.value); });
      setCompareData(rawData);
    })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedMetric, compareMetrics, limit, selectedTenantId, showCompare]);

  const activeMetrics = useMemo(() => {
    return showCompare ? [selectedMetric, ...compareMetrics] : [selectedMetric];
  }, [selectedMetric, compareMetrics, showCompare]);

  const toggleCompareMetric = (m: string) => {
    setCompareMetrics((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : prev.length < 2 ? [...prev, m] : prev
    );
  };

  const PRESETS = [
    { label: t('trending.presetComplianceHardening'), primary: 'compliance_rate', compare: ['hardening_score'] },
    { label: t('trending.presetAlertsHosts'), primary: 'alert_count', compare: ['host_count'] },
  ];

  const applyPreset = (preset: { primary: string; compare: string[] }) => {
    if (metrics.includes(preset.primary)) {
      setSelectedMetric(preset.primary);
      setCompareMetrics(preset.compare.filter((m) => metrics.includes(m)));
      setShowCompare(true);
    }
  };

  const axisStroke = isDark ? '#6b7280' : '#9ca3af';
  const gridStroke = isDark ? '#374151' : '#f0f0f0';
  const tooltipStyle = isDark
    ? { borderRadius: '8px', border: '1px solid #374151', fontSize: '13px', backgroundColor: '#1f2937', color: '#e5e7eb' }
    : { borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' };

  const selectClass = "text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 flex-wrap">
        <select value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value)} className={selectClass}>
          {metrics.length === 0 && <option value="">{t('trending.noMetrics')}</option>}
          {metrics.map((m) => (
            <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className={selectClass}>
          <option value={50}>{t('trending.last50')}</option>
          <option value={100}>{t('trending.last100')}</option>
          <option value={200}>{t('trending.last200')}</option>
          <option value={500}>{t('trending.last500')}</option>
        </select>
        <button
          onClick={() => setShowCompare(!showCompare)}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-colors ${
            showCompare
              ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Layers className="w-4 h-4" /> {t('trending.compare')}
        </button>
        <div className="flex items-center gap-1.5">
          {PRESETS.map((preset, i) => (
            <button
              key={i}
              onClick={() => applyPreset(preset)}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors"
            >
              <Zap className="w-3 h-3" /> {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Compare metric selector */}
      {showCompare && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 dark:text-gray-400">{t('trending.compareWith')}:</span>
          {metrics
            .filter((m) => m !== selectedMetric)
            .map((m, i) => (
              <button
                key={m}
                onClick={() => toggleCompareMetric(m)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors capitalize ${
                  compareMetrics.includes(m)
                    ? 'border-transparent text-white'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                style={compareMetrics.includes(m) ? { backgroundColor: COLORS[(i + 1) % COLORS.length] } : undefined}
              >
                {m.replace(/_/g, ' ')}
              </button>
            ))}
        </div>
      )}

      {loading ? (
        <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />
      ) : chartData.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
          {t('trending.noData')}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 capitalize">
            {activeMetrics.map((m) => m.replace(/_/g, ' ')).join(' vs ')}
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="date" fontSize={12} tickLine={false} stroke={axisStroke} />
              <YAxis fontSize={12} tickLine={false} stroke={axisStroke} />
              <Tooltip contentStyle={tooltipStyle} />
              {activeMetrics.length > 1 && <Legend />}
              {activeMetrics.map((m, i) => (
                <Line
                  key={m}
                  type="monotone"
                  dataKey={m}
                  name={m.replace(/_/g, ' ')}
                  stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray={i > 0 ? '5 3' : undefined}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Metric summary cards */}
      {Object.keys(compareData).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeMetrics.map((m, i) => (
            <MetricSummary
              key={m}
              data={compareData[m] || []}
              label={m.replace(/_/g, ' ')}
              color={COLORS[i % COLORS.length]}
            />
          ))}
        </div>
      )}

      {/* All metrics at a glance */}
      {metrics.length > 1 && !showCompare && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {metrics
            .filter((m) => m !== selectedMetric)
            .slice(0, 4)
            .map((metric, i) => (
              <MiniChart key={metric} metric={metric} color={COLORS[i % COLORS.length]} onClick={() => setSelectedMetric(metric)} />
            ))}
        </div>
      )}
    </div>
  );
}

function MiniChart({ metric, color, onClick }: { metric: string; color: string; onClick: () => void }) {
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    fetchTrendingData(metric, { limit: 30 })
      .then((points: any[]) => setData((points || []).reverse()))
      .catch(console.error);
  }, [metric]);

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
      onClick={onClick}
    >
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize mb-2">
        {metric.replace(/_/g, ' ')}
      </h4>
      <ResponsiveContainer width="100%" height={100}>
        <LineChart data={data}>
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={1.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
